import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Factory, Truck, Cpu, Wrench, ShieldCheck, Handshake } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FadeUp, SectionHeading } from "../components/Shared";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { icon: Factory, title: "Manufacturing Partners", desc: "CNC shops, sheet metal fabricators, molders and foundries looking for consistent, qualified order flow." },
  { icon: Wrench, title: "Finishing & Processing", desc: "Anodizers, plating houses, heat treaters and coating specialists to complete our end-to-end offering." },
  { icon: Truck, title: "Logistics Partners", desc: "Freight, warehousing and last-mile providers supporting reliable domestic and international delivery." },
  { icon: Cpu, title: "Technology Partners", desc: "CAD/CAM, metrology, IoT and software providers integrating with the Vectron platform." },
  { icon: ShieldCheck, title: "Quality & Certification", desc: "Inspection labs, NDT specialists and certification bodies strengthening our quality network." },
  { icon: Handshake, title: "Channel & Referral", desc: "Design firms, consultants and distributors who bring manufacturing demand to the platform." },
];

const PARTNER_TYPES = ["Manufacturing Partner", "Finishing & Processing", "Logistics Partner", "Technology Partner", "Quality & Certification", "Channel & Referral"];

const initialForm = { company_name: "", contact_name: "", email: "", phone: "", partner_type: "", message: "" };

export default function Partners() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email || !form.partner_type) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/enquiries/partner`, form);
      toast.success("Enquiry submitted! Our partnerships team will reach out shortly.");
      setForm(initialForm);
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="partners-page">
      <section className="py-28 sm:py-36 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <FadeUp>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary mb-6 font-mono-tech">Partner Network</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter max-w-3xl leading-[1.05]">
              Grow with the <span className="font-medium text-primary">Vectron network.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              We partner with world-class manufacturers, logistics providers and technology companies to deliver a seamless production experience.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <SectionHeading overline="Who We Partner With" title="Six ways to plug into the platform." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CATEGORIES.map((c, i) => (
              <FadeUp key={c.title} delay={i * 0.06}>
                <div className="h-full p-7 rounded-xl bg-card border border-border transition-transform duration-200 hover:-translate-y-1" data-testid={`partner-category-${i}`}>
                  <c.icon className="h-6 w-6 text-primary mb-5" strokeWidth={1.5} />
                  <h4 className="text-lg font-semibold font-heading">{c.title}</h4>
                  <p className="mt-2.5 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-border bg-primary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-wrap items-center justify-between gap-6" data-testid="partner-portal-cta">
          <div>
            <h3 className="text-xl sm:text-2xl font-medium font-heading tracking-tight">Manufacturing partner? Onboard directly.</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-xl">Create a partner account with your machines and capabilities. Once approved, manage everything from your Partner Portal.</p>
          </div>
          <div className="flex gap-3">
            <Button asChild size="lg" className="rounded-full px-8" data-testid="partner-register-cta-btn">
              <Link to="/partner/register">Register as Partner</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full px-8" data-testid="partner-login-cta-btn">
              <Link to="/partner/login">Partner Login</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-muted/60 border-t border-border" id="partner-form">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <SectionHeading
            overline="Partner Enquiry"
            title="Apply to join the network."
            description="Tell us about your capabilities. Our partnerships team reviews every application within 5 business days."
          />
          <FadeUp>
            <form onSubmit={submit} className="space-y-6 p-8 sm:p-10 rounded-xl bg-card border border-border" data-testid="partner-enquiry-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="p-company">Company Name *</Label>
                  <Input id="p-company" value={form.company_name} onChange={set("company_name")} placeholder="Acme Precision Pvt Ltd" data-testid="partner-company-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="p-name">Contact Name *</Label>
                  <Input id="p-name" value={form.contact_name} onChange={set("contact_name")} placeholder="Full name" data-testid="partner-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="p-email">Email *</Label>
                  <Input id="p-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="partner-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="p-phone">Phone</Label>
                  <Input id="p-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." data-testid="partner-phone-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Partnership Type *</Label>
                <Select value={form.partner_type} onValueChange={(v) => setForm({ ...form, partner_type: v })}>
                  <SelectTrigger data-testid="partner-type-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {PARTNER_TYPES.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`partner-type-option-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-message">Capabilities & Details</Label>
                <Textarea id="p-message" rows={5} value={form.message} onChange={set("message")} placeholder="Machines, certifications, capacity, service areas..." data-testid="partner-message-input" />
              </div>
              <Button type="submit" size="lg" disabled={loading} className="rounded-full px-10" data-testid="partner-submit-btn">
                {loading ? "Submitting..." : "Submit Enquiry"}
              </Button>
            </form>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
